<template>
  <div>
    <b-row class="mb-3" align-h="between" align-v="center">
      <b-col cols="auto">
        <b-button variant="link" class="p-0" @click="$router.push({ name: 'SmMarketingComparison' })">
          &larr; {{ $t('views.smmarketingcomparison.detail.back') }}
        </b-button>
        <h1 class="mt-2">{{ $t('views.smmarketingcomparison.detail.title') }}</h1>
      </b-col>
      <b-col cols="auto">
        <b-button variant="outline-primary" @click="exportExcel">
          {{ $t('views.smmarketingcomparison.button.export') }}
        </b-button>
      </b-col>
    </b-row>

    <b-row class="mb-2" align-v="center">
      <b-col class="text-right">
        <small class="text-muted mr-2">{{ $t('views.smmarketingcomparison.paging.totalLabel') }}</small>
        <b-badge variant="light">{{ totalRows }}</b-badge>
      </b-col>
    </b-row>

    <b-row class="mb-2" align-v="center">
      <b-col class="text-right">
        <b-pagination
          v-model="page"
          :per-page="pageSize"
          :total-rows="totalRows"
          align="right"
          :disabled="isLoading"
          @change="onPageChange"
        />
      </b-col>
    </b-row>

    <b-table
      :items="mismatches"
      :fields="mismatchFields"
      :busy="isLoading"
      striped
      hover
      responsive
      show-empty
      :empty-text="$t('views.smmarketingcomparison.mismatchtable.empty')"
    >
      <template #table-busy>
        <div class="text-center py-4">
          <b-spinner variant="primary" class="mr-2" />
          <span>{{ $t('views.smmarketingcomparison.mismatchtable.loading') }}</span>
        </div>
      </template>

      <template #cell(expand)="row">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="py-0"
          @click="row.toggleDetails"
        >
          {{ row.detailsShowing
            ? $t('views.smmarketingcomparison.mismatchtable.collapse')
            : $t('views.smmarketingcomparison.mismatchtable.expand') }}
        </b-button>
      </template>

      <template #row-details="row">
        <div class="p-3 bg-light border-top">
          <template v-if="getFieldDiffs(row.item).length">
            <b-table
              small
              bordered
              :items="getFieldDiffs(row.item)"
              :fields="diffFields"
              thead-class="thead-light"
              class="mb-0 bg-white"
            >
              <template #cell(fieldLabel)="data">
                {{ data.value || '—' }}
              </template>
              <template #cell(locationValue)="data">
                <span :class="{ 'text-muted': !data.value }">{{ data.value || '—' }}</span>
              </template>
              <template #cell(smValue)="data">
                <span :class="{ 'text-muted': !data.value }">{{ data.value || '—' }}</span>
              </template>
            </b-table>
          </template>
          <p v-else class="mb-0 text-muted">
            {{ $t('views.smmarketingcomparison.mismatchtable.noFieldDiffs') }}
          </p>
        </div>
      </template>
    </b-table>

    <b-row class="mt-3" align-v="center">
      <b-col class="text-right">
        <b-pagination
          v-model="page"
          :per-page="pageSize"
          :total-rows="totalRows"
          align="right"
          :disabled="isLoading"
          @change="onPageChange"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { parseFieldDiffs } from '@/assets/js/helper/smmarketingcomparison'

const PAGE_SIZE = 100

function normalizeDiffRows (diffs) {
  if (!Array.isArray(diffs)) {
    return []
  }

  return diffs.map(diff => ({
    field: diff.field ?? diff.Field ?? '',
    fieldLabel: diff.fieldLabel ?? diff.FieldLabel ?? diff.field ?? diff.Field ?? '',
    locationValue: diff.locationValue ?? diff.LocationValue ?? '',
    smValue: diff.smValue ?? diff.SmValue ?? ''
  }))
}

export default {
  name: 'SmMarketingComparison.Detail',
  data () {
    return {
      page: 1,
      pageSize: PAGE_SIZE,
      mismatchFields: [
        { key: 'expand', label: '', class: 'text-nowrap', thStyle: { width: '1%' } },
        { key: 'smMarketingId', label: this.$t('views.smmarketingcomparison.mismatchtable.label.smMarketingId') },
        { key: 'customerId', label: this.$t('views.smmarketingcomparison.mismatchtable.label.customerId') },
        { key: 'locationName', label: this.$t('views.smmarketingcomparison.mismatchtable.label.locationName') },
        { key: 'mismatchType', label: this.$t('views.smmarketingcomparison.mismatchtable.label.mismatchType') },
        { key: 'addressMatchType', label: this.$t('views.smmarketingcomparison.mismatchtable.label.addressMatchType') },
        { key: 'differingFields', label: this.$t('views.smmarketingcomparison.mismatchtable.label.differingFields') }
      ],
      diffFields: [
        { key: 'fieldLabel', label: this.$t('views.smmarketingcomparison.mismatchtable.diff.label.field') },
        { key: 'locationValue', label: this.$t('views.smmarketingcomparison.mismatchtable.diff.label.location') },
        { key: 'smValue', label: this.$t('views.smmarketingcomparison.mismatchtable.diff.label.sm') }
      ]
    }
  },
  computed: {
    runId () {
      return this.$route.params.runid
    },
    isLoading () {
      return this.$store.state.smmarketingcomparison.isLoading
    },
    mismatches () {
      return this.$store.state.smmarketingcomparison.mismatches
    },
    totalRows () {
      return this.$store.state.smmarketingcomparison.mismatchesTotalItems
    }
  },
  methods: {
    getFieldDiffs (item) {
      return normalizeDiffRows(parseFieldDiffs(item))
    },
    exportExcel () {
      this.$store.dispatch('smmarketingcomparison/downloadExcel', this.runId)
        .then(response => {
          if (!response || !response.data) return
          const contentDisposition = response.headers['content-disposition']
          let filename = 'SM_Marketing_Abgleich.xlsx'
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename\*?="?([^;"]+)/)
            if (filenameMatch && filenameMatch.length > 1) {
              filename = filenameMatch[1]
            }
          }
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', filename)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
    },
    loadMismatches () {
      return this.$store.dispatch('smmarketingcomparison/loadMismatches', {
        runId: this.runId,
        page: this.page,
        pageSize: this.pageSize
      })
    },
    onPageChange (newPage) {
      this.page = newPage
      this.loadMismatches()
    }
  },
  created () {
    this.$store.commit('smmarketingcomparison/RESET_MISMATCHES')
    this.page = 1
    this.loadMismatches()
  }
}
</script>
